import { MapPinIcon, PencilSquareIcon, PhoneIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { LoginResponse } from "../../Auth/models/LoginResponse";
import { AuthService } from "../../Auth/services/AuthService";
import { GenericImage } from "../../../Shared/components/GenericImage";
import { UserForm } from "../../Users/components/UserForm";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { UserService } from "../../Users/services/user.service";
import { useQuery } from "@tanstack/react-query";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { useTranslation } from "react-i18next";

export const Settings: React.FC = () => {
  const [searchParams] = useSearchParams();
  const user = searchParams.get("user");
  const {t} = useTranslation()
  const message = t('loading_message');
  const isAllowed = AuthService.isManagementAllowed();

  const currentUser: LoginResponse | null = AuthService.getLocalUser();
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();

  const findUserAccount = async () => {
    try {
      setIsLoading(true);
      return await UserService.getById(user ?? currentUser?.user?.id ?? "");
    } catch (error: any) {
      setIsLoading(false);
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const query = useQuery({
    queryKey: ["account"],
    queryFn: findUserAccount,
    placeholderData: (previous) => previous?.data,
  });

  // Create User
  const [isModalOpen, setIsModalOpen] = useState(false);
  const onAddAction = () => setIsModalOpen(!isModalOpen);
  const toggleModal = (success: boolean) => {
    setIsModalOpen(!isModalOpen);
    if (success) {
      query.refetch();
    }
  };

  return (
    <>
      {isModalOpen && <UserForm onClose={toggleModal} user={query.data} />}
      {isLoading && <OverlayLoader message={message} />}

      <section className="antialiased dark:bg-gray-900">
        <div className="mx-auto max-w-screen-lg px-4 2xl:px-0">
          <div className="py-4 md:py-8">
            <div className="mb-4 grid gap-4 sm:grid-cols-2 sm:gap-8 lg:gap-16">
              <div className="space-y-4">
                <div className="flex space-x-4">
                  <div className="p-3 bg-gray-400 rounded">
                    <GenericImage />
                  </div>
                  <div>
                    <span className="mb-2 inline-block rounded bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800 dark:bg-primary-900 dark:text-primary-300">
                      {query.data?.roles?.[0]?.name || t('user')}
                    </span>
                    <h2 className="flex items-center text-xl font-bold leading-none text-gray-900 dark:text-white sm:text-2xl">
                      {UtilService.titleCase(query.data?.firstName ?? "")}{" "}
                      {UtilService.titleCase(query.data?.middleName ?? "")}{" "}
                      {UtilService.titleCase(query.data?.lastName ?? "")}
                    </h2>
                  </div>
                </div>
                <dl className="">
                  <dt className="font-semibold text-gray-900 dark:text-white">
                    {t('email_label')}
                  </dt>
                  <dd className="text-gray-500 dark:text-gray-400">
                    {query.data?.email}
                  </dd>
                </dl>
                <dl>
                  <dt className="font-semibold text-gray-900 dark:text-white">
                    {t('phone_number_label')}
                  </dt>
                  <dd className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                    <PhoneIcon className="h-4 w-4" />
                    {query.data?.phoneNumber || t('no_phone')}
                  </dd>
                </dl>
              </div>
              <div className="space-y-4">
                <dl>
                  <dt className="font-semibold text-gray-900 dark:text-white">
                  {t('address')} 
                  </dt>
                  <dd className="text-gray-500 dark:text-gray-400">
                    {query.data?.country ? query.data?.country : t('no_country_address')}
                  </dd>
                </dl>
                <dl>
                  <dt className="font-semibold text-gray-900 dark:text-white">
                    {t('user_roles')}
                  </dt>
                  <dd className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                    {query.data?.roles?.length ? (
                      <ol type="1" className="list-disc list-inside">
                        {query.data.roles.map((role: any, index: number) => (
                          <li key={index}>{role.name}</li>
                        ))}
                      </ol>
                    ) : (
                      t('user')
                    )}
                  </dd>
                </dl>
                {!isAllowed && (
                  <dl>
                    <dt className="font-semibold text-gray-900 dark:text-white">
                      {t('my_companies')}
                    </dt>
                    <div className="text-gray-500 dark:text-gray-400">
                      {query.data?.companies?.length ? (
                        <ol type="1" className="list-disc list-inside">
                          {query.data.companies.map(
                            (company: any, index: number) => (
                              <li key={index}>{company.name}</li>
                            )
                          )}
                        </ol>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            (window.location.href =
                              "/services/accounts/companies")
                          }
                          className="text-primary-700 hover:underline"
                        >
                          {t('create_company')}
                        </button>
                      )}
                    </div>
                  </dl>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onAddAction}
              className="inline-flex w-full items-center justify-center rounded-lg bg-primary-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-800 focus:outline-none focus:ring-4 focus:ring-primary-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 sm:w-auto"
            >
              <PencilSquareIcon className="h-4 w-4 mr-2" />
              {t('edit_account')}
            </button>
          </div>

          {/* <div className="grid grid-cols-2 gap-6 border-b border-t border-gray-200 py-4 dark:border-gray-700 md:py-8 lg:grid-cols-4 xl:gap-16">
            <div>
              <h3 className="mb-2 text-gray-500 dark:text-gray-400">
                Orders made
              </h3>
              <span className="flex items-center text-2xl font-bold text-gray-900 dark:text-white">
                24
                <span className="ms-2 inline-flex items-center rounded bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-300">
                  <svg
                    className="-ms-1 me-1 h-4 w-4"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v13m0-13 4 4m-4-4-4 4"
                    ></path>
                  </svg>
                  10.3%
                </span>
              </span>
              <p className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400 sm:text-base">
                <svg
                  className="me-1.5 h-4 w-4 text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 11h2v5m-2 0h4m-2.592-8.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
                vs 20 last 3 months
              </p>
            </div>
            <div>
              <h3 className="mb-2 text-gray-500 dark:text-gray-400">
                Reviews added
              </h3>
              <span className="flex items-center text-2xl font-bold text-gray-900 dark:text-white">
                16
                <span className="ms-2 inline-flex items-center rounded bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-300">
                  <svg
                    className="-ms-1 me-1 h-4 w-4"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v13m0-13 4 4m-4-4-4 4"
                    ></path>
                  </svg>
                  8.6%
                </span>
              </span>
              <p className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400 sm:text-base">
                <svg
                  className="me-1.5 h-4 w-4 text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 11h2v5m-2 0h4m-2.592-8.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
                vs 14 last 3 months
              </p>
            </div>
            <div>
              <h3 className="mb-2 text-gray-500 dark:text-gray-400">
                Favorite products added
              </h3>
              <span className="flex items-center text-2xl font-bold text-gray-900 dark:text-white">
                8
                <span className="ms-2 inline-flex items-center rounded bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-300">
                  <svg
                    className="-ms-1 me-1 h-4 w-4"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v13m0-13 4 4m-4-4-4 4"
                    ></path>
                  </svg>
                  12%
                </span>
              </span>
              <p className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400 sm:text-base">
                <svg
                  className="me-1.5 h-4 w-4 text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 11h2v5m-2 0h4m-2.592-8.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
                vs 10 last 3 months
              </p>
            </div>
            <div>
              <h3 className="mb-2 text-gray-500 dark:text-gray-400">
                Product returns
              </h3>
              <span className="flex items-center text-2xl font-bold text-gray-900 dark:text-white">
                2
                <span className="ms-2 inline-flex items-center rounded bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-300">
                  <svg
                    className="-ms-1 me-1 h-4 w-4"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v13m0-13 4 4m-4-4-4 4"
                    ></path>
                  </svg>
                  50%
                </span>
              </span>
              <p className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400 sm:text-base">
                <svg
                  className="me-1.5 h-4 w-4 text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 11h2v5m-2 0h4m-2.592-8.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
                vs 1 last 3 months
              </p>
            </div>
          </div> */}
        </div>
      </section>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
    </>
  );
};
